import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  Alert02Icon, Archive02Icon, ArrowDown01Icon, ArrowLeft01Icon, ArrowLeft02Icon,
  ArrowMoveDownRightIcon, ArrowRight01Icon, ArrowRight02Icon, ArrowUp01Icon, ArrowUpRight01Icon,
  Briefcase01Icon, BubbleChatIcon, Calendar01Icon, Calendar03Icon, CalendarAdd01Icon,
  CalendarCheckIn01Icon, Cancel01Icon, CheckmarkBadge01Icon, CheckmarkCircle02Icon, ClipboardIcon,
  Clock01Icon, CloudIcon, CloudSavingDone01Icon, Coins01Icon, CommandIcon, ComputerIcon, Copy01Icon,
  CornerDownLeftIcon, Database01Icon, Delete02Icon, Download01Icon, File01Icon, FileUploadIcon,
  Folder01Icon, Home01Icon, IdentityCardIcon, InboxIcon, InformationCircleIcon, Invoice01Icon,
  Link01Icon, Location01Icon, Login01Icon, Logout01Icon, MinusSignIcon, MoneyReceiveCircleIcon, Moon02Icon,
  MoreHorizontalIcon, PencilEdit01Icon, PercentCircleIcon, PieChartIcon, PlayIcon, PlusSignIcon, PrinterIcon,
  RefreshIcon, RepeatIcon, RssIcon, Search01Icon, SentIcon, Settings01Icon, SquareLock02Icon,
  StopIcon, Sun03Icon, Target02Icon, Tick02Icon, Undo02Icon, Upload01Icon, UserIcon, ViewIcon,
  ViewOffSlashIcon, ZoomInAreaIcon, ZoomOutAreaIcon
} from "@hugeicons/core-free-icons";

const ICONS = {
  archive: Archive02Icon,
  "arrow-left": ArrowLeft02Icon,
  "arrow-right": ArrowRight02Icon,
  "arrow-up-right": ArrowUpRight01Icon,
  briefcase: Briefcase01Icon,
  calendar: Calendar03Icon,
  "calendar-check": CalendarCheckIn01Icon,
  "calendar-dots": Calendar01Icon,
  "calendar-plus": CalendarAdd01Icon,
  "caret-down": ArrowDown01Icon,
  "caret-left": ArrowLeft01Icon,
  "caret-right": ArrowRight01Icon,
  "caret-up": ArrowUp01Icon,
  chat: BubbleChatIcon,
  check: Tick02Icon,
  "check-circle": CheckmarkCircle02Icon,
  clipboard: ClipboardIcon,
  clock: Clock01Icon,
  copy: Copy01Icon,
  eye: ViewIcon,
  "eye-off": ViewOffSlashIcon,
  link: Link01Icon,
  location: Location01Icon,
  lock: SquareLock02Icon,
  minus: MinusSignIcon,
  repeat: RepeatIcon,
  rss: RssIcon,
  "zoom-in": ZoomInAreaIcon,
  "zoom-out": ZoomOutAreaIcon,
  close: Cancel01Icon,
  cloud: CloudIcon,
  "cloud-check": CloudSavingDone01Icon,
  coins: Coins01Icon,
  command: CommandIcon,
  database: Database01Icon,
  dots: MoreHorizontalIcon,
  download: Download01Icon,
  "file-up": FileUploadIcon,
  "file-text": File01Icon,
  folder: Folder01Icon,
  "hand-coins": MoneyReceiveCircleIcon,
  house: Home01Icon,
  "id-card": IdentityCardIcon,
  info: InformationCircleIcon,
  monitor: ComputerIcon,
  moon: Moon02Icon,
  pencil: PencilEdit01Icon,
  percent: PercentCircleIcon,
  "pie-chart": PieChartIcon,
  play: PlayIcon,
  plus: PlusSignIcon,
  printer: PrinterIcon,
  receipt: Invoice01Icon,
  refresh: RefreshIcon,
  "return": CornerDownLeftIcon,
  search: Search01Icon,
  "seal-check": CheckmarkBadge01Icon,
  send: SentIcon,
  settings: Settings01Icon,
  "sign-in": Login01Icon,
  "sign-out": Logout01Icon,
  stop: StopIcon,
  subtask: ArrowMoveDownRightIcon,
  sun: Sun03Icon,
  target: Target02Icon,
  trash: Delete02Icon,
  tray: InboxIcon,
  undo: Undo02Icon,
  upload: Upload01Icon,
  user: UserIcon,
  warning: Alert02Icon
} satisfies Record<string, IconSvgElement>;

export type IconName = keyof typeof ICONS;
export type IconWeight = "regular" | "bold" | "fill";

const STROKE: Record<IconWeight, number> = { regular: 1.5, bold: 2, fill: 1.5 };

/**
 * Hugeicons stroke-rounded glyphs on one 1.5 line, so every icon in ontrack shares a stroke.
 * `bold` thickens it for small marks such as carets and checks; `fill` makes closed shapes
 * (play, stop) solid.
 */
export function Icon({ name, className = "", weight = "regular" }: { name: IconName; className?: string; weight?: IconWeight }) {
  return <HugeiconsIcon
    icon={ICONS[name]}
    strokeWidth={STROKE[weight]}
    fill={weight === "fill" ? "currentColor" : "none"}
    aria-hidden="true"
    data-icon={name}
    className={`ot-icon ${className}`.trim()}
  />;
}
